/**
 * @preserve
 * Licenses of bundled dependencies can be found in dependencies-licenses.txt
 */

import { r as registerInstance, d as createEvent, h, c as Host, g as getElement } from './index-B0vaMlyg.js';
import { i as isKeyboardClickOnFocusedElement } from './is-keyboard-click-on-focused-element-BgVnoOeI.js';
import { t as transformTagNames } from './transform-tag-name-B5E60fj5.js';
import { D as DebounceDelays } from './debounce-constants-DjdUiR4p.js';
import { g as getDataOnFromTheme } from './themes-DVWPp0eL.js';
import { d as debounce } from './debounce-CydJ87cs.js';
import { g as getRandomId } from './get-random-id-Y35SgnA8.js';
import { i as isBaseCharsEqual } from './is-base-chars-equal-CRFJa7Tm.js';
import { q as querySelectorAllAsArray } from './query-selector-all-as-array-BMNvfiXu.js';
import { a as SharedDirectionService } from './init-services-CKZT6dhM.js';
import './keys-C-its6Yu.js';
import './direction.service-g9OBd7A0.js';

const tabCss = ":host{--tab-icon-color:var(--wb-tabs-unselected-icon-leading-color-default);--tab-icon-size:var(--wb-tabs-unselected-icon-leading-size);--tab-text-color:var(--wb-tabs-unselected-text-color-default);--tab-background-color:var(--wb-tabs-unselected-background-color-default);--tab-border:var(--wb-tabs-unselected-border-weight) solid transparent;--tab-padding-block:calc(var(--wb-tabs-padding-top) - var(--wb-tabs-unselected-border-weight)) calc(var(--wb-tabs-padding-bottom) - var(--wb-tabs-unselected-border-weight));--tab-padding-inline:calc(var(--wb-tabs-padding-left) - var(--wb-tabs-unselected-border-weight)) calc(var(--wb-tabs-padding-right) - var(--wb-tabs-unselected-border-weight));--tab-gap:var(--wb-tabs-unselected-gap);--tab-opacity-disabled:var(--wb-form-control-effects-opacity-disabled);background-color:var(--tab-background-color);border:var(--tab-border);outline:none;display:block;cursor:pointer;min-width:5rem;width:auto;color:var(--tab-text-color);text-align:center;white-space:nowrap;padding-block:var(--tab-padding-block);padding-inline:var(--tab-padding-inline)}:host(:hover:not([disabled])){--tab-icon-color:var(--wb-tabs-unselected-icon-leading-color-hover);--tab-text-color:var(--wb-tabs-unselected-text-color-hover);--tab-background-color:var(--wb-tabs-unselected-background-color-hover);transition:background-color 0.3s ease-in-out}.text{display:flex;align-items:center;justify-content:center;gap:var(--tab-gap)}::slotted(.wb-icon){flex-shrink:0;--size:var(--tab-icon-size);color:var(--tab-icon-color)}:host(:focus){--tab-icon-color:var(--wb-tabs-unselected-icon-leading-color-focus);--tab-text-color:var(--wb-tabs-unselected-text-color-focus);--tab-background-color:var(--wb-tabs-unselected-background-color-focus);--tab-border:var(--wb-tabs-unselected-border-weight) solid var(--wb-tabs-unselected-border-ring-color-focus)}:host([disabled]){opacity:var(--tab-opacity-disabled);cursor:not-allowed}:host([icon-only]){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-default);--tab-icon-size:var(--wb-tabs-unselected-icon-only-size)}:host([icon-only]) .text{gap:0}:host([icon-only]) .wb-text{font-size:0}:host([icon-only]:hover:not([disabled])){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-hover)}:host([icon-only]:focus){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-hover)}:host([selected]){--tab-icon-color:var(--wb-tabs-selected-icon-leading-color-default);--tab-icon-size:var(--wb-tabs-selected-icon-leading-size);--tab-text-color:var(--wb-tabs-selected-text-color-default);--tab-background-color:var(--wb-tabs-selected-background-color-default)}:host([selected]:hover:not([disabled])){--tab-background-color:var(--wb-tabs-selected-background-color-hover)}:host([selected]:active:not([disabled])){--tab-background-color:var(--wb-tabs-selected-background-color-active)}:host([selected]:focus){--tab-background-color:var(--wb-tabs-selected-background-color-focus);--tab-border:var(--wb-tabs-selected-border-weight) solid var(--wb-tabs-selected-border-ring-color-focus)}:host([selected][icon-only]){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-default);--tab-icon-size:var(--wb-tabs-unselected-icon-only-size)}:host([selected][icon-only]:hover:not([disabled])){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-hover)}:host([selected][icon-only]:focus){--tab-icon-color:var(--wb-tabs-unselected-icon-only-color-focus)}";

const TabComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.wbnexttab = createEvent(this, "wbnexttab", 7);
        this.wbprevioustab = createEvent(this, "wbprevioustab", 7);
        /** If present, shows the tab as disabled */
        this.disabled = false;
        /** If present, hides the text by setting the font-size to 0 (for a11y) */
        this.iconOnly = false;
        /** @internal apply to render element, set by wb-tabs */
        this.selected = false;
        this.TagNames = transformTagNames(this, 'wb-tab', ['wb-text', 'wb-tabs']);
        this.updateTabsHighlight = () => {
            this.tabsEl.updateTabsHighlight();
        };
    }
    /** @internal called by parent <wb-tabs> to enforce propagation of theme changes */
    async updateTheme() {
        this.inheritAttributes();
    }
    handleKeyDown(ev) {
        if (isKeyboardClickOnFocusedElement(this.el, ev)) {
            ev.preventDefault();
            this.el.click();
        }
        else {
            switch (ev.key) {
                case 'ArrowRight':
                    ev.preventDefault();
                    this.wbnexttab.emit({ tab: this.el });
                    break;
                case 'ArrowLeft':
                    ev.preventDefault();
                    this.wbprevioustab.emit({ tab: this.el });
                    break;
            }
        }
    }
    connectedCallback() {
        this.tabsEl = this.el.closest(this.TagNames.WbTabs);
    }
    componentWillLoad() {
        this.inheritAttributes();
    }
    inheritAttributes() {
        this.el.setAttribute('theme', this.tabsEl.theme);
    }
    render() {
        const { WbText } = this.TagNames;
        return (h(Host, { key: 'da57e15c5ac8acafc4943e7392985f5b12f1dd2b', class: "wb-tab", role: "tab", tabindex: this.disabled ? null : '0', "aria-selected": String(this.selected) }, h(WbText, { key: 'deab1c8b68a8f1bb90cc76de559ea9862f0a48ac', size: "l", static: true, strong: true }, h("div", { key: 'b6edb41e9a0b97526debbb514b0036135ea1debf', class: "text" }, h("slot", { key: 'a0aa369780633b67241dc08d81af6f61d6574778', onSlotchange: this.updateTabsHighlight })))));
    }
    get el() { return getElement(this); }
};
TabComponent.style = tabCss;

const tabContentCss = ":host{padding:var(--wb-spacing-s) 0;display:none}:host([selected]){display:block}";

const TabContentComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        /** @internal apply to render element, set by wb-tabs */
        this.selected = false;
    }
    render() {
        return (h(Host, { key: '3234a37eca70b1ff83ccb3fae14786ae91026d1c', class: "wb-tab-content", role: "tabpanel" }, h("slot", { key: '3d54221dd3ff580aa6b1cb39daf7d9dc36924c48' })));
    }
};
TabContentComponent.style = tabContentCss;

function assignIdToAttribute(targetElement, attribute, id) {
    // already configured/set attributes have priority over automatically generated ones!
    targetElement.setAttribute(attribute, targetElement.getAttribute(attribute) || id);
}
function constructId(namespace, name, type) {
    return `${namespace}__${name}__${type}`;
}
function connectTabAndContent(namespace, tabEl, tabContentEl) {
    assignIdToAttribute(tabEl, 'id', constructId(namespace, tabEl.name, 'tab'));
    assignIdToAttribute(tabEl, 'aria-controls', constructId(namespace, tabEl.name, 'content'));
    assignIdToAttribute(tabContentEl, 'id', constructId(namespace, tabContentEl.name, 'content'));
    assignIdToAttribute(tabContentEl, 'aria-labelledby', constructId(namespace, tabContentEl.name, 'tab'));
}

const tabsCss = ":host{--tabs-border-underline:var(--wb-tabs-unselected-border-weight) solid var(--wb-tabs-unselected-border-underline-color-default);--tabs-gap:var(--wb-tabs-unselected-gap);--tabs-marker-color:var(--wb-tabs-selected-marker-color-default);--tabs-marker-weight:var(--wb-tabs-selected-marker-weight);display:block;position:relative}.inner{border-block-end:var(--tabs-border-underline)}.wrapper{display:flex;flex-direction:row;position:relative;gap:var(--tabs-gap)}.highlight{position:absolute;inset-block-end:0;inset-inline-start:0;height:var(--tabs-marker-weight);background:var(--tabs-marker-color);transition:all 0.3s var(--wb-move);margin-inline-start:calc(-1 * var(--wb-tabs-unselected-border-weight))}@media (prefers-reduced-motion){.highlight{transition:transform 0s}}:host([is-rtl]) .highlight{inset-inline-start:unset;inset-inline-end:0}:host([fitted]) .wrapper{padding-inline-start:0;flex:1}:host([fitted]) ::slotted(.wb-tab){flex:1}";

const TabsComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.wbchange = createEvent(this, "wbchange", 7);
        /** namespace for element and all its children */
        this.namespace = getRandomId();
        /** apply to make wb-tab elements take up all available space */
        this.fitted = false;
        /** sets aria-label for the button that scrolls towards the end */
        this.scrollToEndButtonTitle = 'scroll to end';
        /** sets aria-label for the button that scrolls towards the start */
        this.scrollToStartButtonTitle = 'scroll to start';
        /** @internal */
        this.isRtl = false;
        this.TagNames = transformTagNames(this, 'wb-tabs', ['wb-tab', 'wb-tab-content', 'wb-horizontal-scroll']);
        this.tabEls = [];
        this.tabContentEls = [];
        this.debounceUpdateHighlight = debounce(() => this.updateHighlightedTab(), DebounceDelays.Resize);
        this.bindHighlightRef = (el) => {
            this.highlightEl = el;
        };
        this.bindListRef = (el) => {
            this.listEl = el;
        };
        this.setTabEls = () => {
            this.tabEls = querySelectorAllAsArray(this.el, this.TagNames.WbTab);
        };
        this.updateTabEls = () => {
            this.setTabEls();
            this.debounceUpdateHighlight();
        };
        this.updateTabContentEls = () => {
            this.tabContentEls = querySelectorAllAsArray(this.el, this.TagNames.WbTabContent);
        };
        this.setRef = (el) => {
            el.addEventListener('wbchange', (evt) => {
                evt.stopPropagation(); // stop wbchange event propagation from content
            });
        };
    }
    /** @internal called by child <wb-tab> to highlight first tab on tabs changed */
    async updateTabsHighlight() {
        this.debounceUpdateHighlight();
    }
    onSelectedChange(newSelected, prevSelected) {
        // fix for #2881: wait for potential dynamically rendered wb-tab elements to be rendered. otherwise we would early return
        requestAnimationFrame(() => {
            const hasUserClearedSelection = prevSelected && !newSelected;
            if (hasUserClearedSelection) {
                this.reset();
                return;
            }
            const newSelectedTabEl = this.tabEls.find((el) => isBaseCharsEqual(el.name, newSelected));
            // change nothing, when user tries to select disabled or non-existing tab
            if (!newSelectedTabEl || newSelectedTabEl.disabled) {
                return;
            }
            this.selected = newSelectedTabEl.name;
            this.wbchange.emit(this.selected);
            this.updateChildElements();
        });
    }
    updateChildElements() {
        this.tabEls.forEach((el) => (el.selected = isBaseCharsEqual(el.name, this.selected)));
        this.tabContentEls.forEach((el) => (el.selected = isBaseCharsEqual(el.name, this.selected)));
        this.updateHighlightedTab();
    }
    onClick(event) {
        const composedPath = event.composedPath();
        const tabEl = composedPath.find((el) => el.tagName && el.tagName.match(new RegExp(`^${this.TagNames.WbTab}$`, 'i')));
        if (tabEl && !tabEl.disabled) {
            this.selected = tabEl.name;
        }
    }
    onResize() {
        this.debounceUpdateHighlight();
    }
    updateDirection() {
        this.isRtl = SharedDirectionService.isRtl;
    }
    moveFocusToNextTab(event) {
        this.moveFocusToClosestTab(1, event.detail.tab);
    }
    moveFocusToPreviousTab(event) {
        this.moveFocusToClosestTab(-1, event.detail.tab);
    }
    moveFocusToTab(targetTab) {
        if (targetTab && !targetTab.disabled) {
            targetTab.focus();
        }
    }
    moveFocusToClosestTab(direction, currentTab) {
        this.setTabEls();
        const tabIndex = this.tabEls.indexOf(currentTab);
        let closestTabIndex = (tabIndex + direction + this.tabEls.length) % this.tabEls.length; // if current tab is first tab jump to last
        while (this.tabEls[closestTabIndex].disabled && closestTabIndex !== tabIndex) {
            closestTabIndex = (closestTabIndex + direction + this.tabEls.length) % this.tabEls.length;
        }
        this.moveFocusToTab(this.tabEls[closestTabIndex]);
    }
    connectedCallback() {
        if (this.selected === '') {
            // if selected attribute is present but empty, select the first tab
            this.setTabEls();
            this.selected = this.tabEls[0]?.name;
        }
    }
    componentDidLoad() {
        this.connectTabsAndContents();
        if (this.selected) {
            this.updateChildElements();
        }
    }
    connectTabsAndContents() {
        this.tabEls.forEach((tabEl) => {
            const tabContentEl = this.tabContentEls.find((tabContentEl) => isBaseCharsEqual(tabContentEl.name, tabEl.name));
            if (tabContentEl) {
                connectTabAndContent(this.namespace, tabEl, tabContentEl);
            }
        });
    }
    reset() {
        // note: this.selected is always null/undefined at this point
        this.resetTabBar();
        this.tabEls.forEach((el) => (el.selected = false));
        this.tabContentEls.forEach((el) => (el.selected = false));
        this.wbchange.emit(this.selected);
    }
    resetTabBar() {
        if (this.highlightEl) {
            this.highlightEl.style.width = '0px';
        }
    }
    updateHighlightedTab() {
        const selectedElement = this.tabEls.find((el) => isBaseCharsEqual(el.name, this.selected));
        // workaround for core#1396
        if (!this.highlightEl || !selectedElement) {
            this.resetTabBar();
            return;
        }
        const offsetDiff = this.isRtl ? this.el.offsetWidth - this.listEl.offsetWidth : 0;
        const offsetLeft = selectedElement.offsetLeft - offsetDiff;
        this.highlightEl.style[this.isRtl ? 'insetInlineEnd' : 'insetInlineStart'] = `${offsetLeft}px`;
        this.highlightEl.style[!this.isRtl ? 'insetInlineEnd' : 'insetInlineStart'] = 'initial';
        this.highlightEl.style.width = `${selectedElement.offsetWidth}px`;
    }
    render() {
        const { WbHorizontalScroll } = this.TagNames;
        return (h(Host, { key: 'a9f1e6d356e139e5679ccbc23d26f40ccb3f2980', class: "wb-tabs", "data-on": getDataOnFromTheme(this.el, this.theme) }, h("div", { key: '6d0314654affe040a5b94b94c778c92e560f7dcf', class: "inner" }, h(WbHorizontalScroll //
        , { key: '731e70c8e4942a376a45ffeb2cf90057dcba6546', scrollToEndButtonTitle: this.scrollToEndButtonTitle, scrollToStartButtonTitle: this.scrollToStartButtonTitle }, h("div", { key: 'a9512d04562910c328de5bf8343eb947542d9f3a', role: "tablist", class: "wrapper", ref: this.bindListRef }, h("slot", { key: '374e975ef94c119a841185865856804f801f4cb4', onSlotchange: this.updateTabEls }), h("div", { key: '40e5b3ba1f93d15f4b19ea992c958eb4980893e4', class: "highlight", ref: this.bindHighlightRef })))), h("slot", { key: '9cbd3177e8aeab0af87b9a72a63b51b906894a1b', name: "content", onSlotchange: this.updateTabContentEls, ref: this.setRef })));
    }
    get el() { return getElement(this); }
    static get watchers() { return {
        "selected": ["onSelectedChange"]
    }; }
};
TabsComponent.style = tabsCss;

export { TabComponent as wb_tab, TabContentComponent as wb_tab_content, TabsComponent as wb_tabs };
//# sourceMappingURL=wb-tab.wb-tab-content.wb-tabs.entry.js.map
